import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';

interface QuickLink {
  label: string;
  path: string;
  shortcut?: string;
}

const QUICK_LINKS: QuickLink[] = [
  { label: 'Compose', path: '/scout/compose', shortcut: 'C' },
  { label: 'Runs', path: '/scout/runs', shortcut: 'R' },
  { label: 'Leads', path: '/scout/export', shortcut: 'L' },
  { label: 'Workspace Criteria', path: '/scout/suppressions', shortcut: 'U' },
  { label: 'Settings › Keys', path: '/settings', shortcut: 'K' },
  { label: 'Settings', path: '/settings', shortcut: 'S' },
  { label: 'Status', path: '/org/status', shortcut: 'T' },
];

interface JumpDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function JumpDialog({ isOpen, onClose }: JumpDialogProps) {
  const navigate = useNavigate();
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  const previousActiveElement = useRef<Element | null>(null);

  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement;
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      firstFocusableRef.current?.focus();
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    onClose();
    if (previousActiveElement.current instanceof HTMLElement) {
      previousActiveElement.current.focus();
    }
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose();
        return;
      }

      if (event.key === 'Tab') {
        const focusableElements = dialogRef.current?.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusableElements?.length) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }

      if (document.activeElement?.tagName !== 'INPUT') {
        const link = QUICK_LINKS.find(
          (item) => item.shortcut?.toLowerCase() === event.key.toLowerCase()
        );
        if (link) {
          event.preventDefault();
          navigate({ to: link.path });
          handleClose();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose, navigate]);

  if (!isOpen) return null;

  const handleNavigate = (path: string) => {
    navigate({ to: path });
    handleClose();
  };

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-bg/80 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Jump to page"
        className="fixed left-1/2 top-1/4 z-50 w-full max-w-md -translate-x-1/2 rounded-lg border border-border bg-surface shadow-lg"
      >
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-text">Jump to</h2>
            <button
              onClick={handleClose}
              className="btn-ghost p-1.5 rounded"
              aria-label="Close dialog"
            >
              <svg
                className="h-5 w-5 text-muted"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-1">
            <p className="text-xs text-muted uppercase tracking-wide mb-2">Pages</p>
            {QUICK_LINKS.map((link, index) => (
              <button
                key={link.label}
                ref={index === 0 ? firstFocusableRef : undefined}
                onClick={() => handleNavigate(link.path)}
                className="w-full flex items-center justify-between px-3 py-2 rounded hover:bg-bg transition-colors duration-fast focus:outline-none focus:ring-2 focus:ring-secondary"
              >
                <span className="text-text">{link.label}</span>
                {link.shortcut && (
                  <kbd className="text-xs text-muted bg-bg px-1.5 py-0.5 rounded border border-border">
                    {link.shortcut}
                  </kbd>
                )}
              </button>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-border flex items-center justify-center gap-2">
            <kbd className="text-xs text-muted bg-bg px-1.5 py-0.5 rounded border border-border">
              ESC
            </kbd>
            <span className="text-xs text-muted">to close</span>
          </div>
        </div>
      </div>
    </>
  );
}
